import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Search,
  Clear,
  Person,
  Description,
  FilterList,
  Check,
} from '@mui/icons-material';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useSearchProvidersQuery } from '../store/hooks';
import {
  useGetAdminNoteTemplatesQuery,
  useConvertAdminNoteTemplateToOrgMutation,
} from '../store/hooks';
import type { AdminNoteTemplateInfo } from '../store/noteTemplatesAdminApi';
import { useSnackbar } from 'notistack';

interface TemplateDiscoveryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => void;
  organizationId: string;
  existingTemplateIds?: string[];
}

export function TemplateDiscoveryDialog({
  open,
  onClose,
  onSelect,
  organizationId,
  existingTemplateIds = [],
}: TemplateDiscoveryDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AdminNoteTemplateInfo | null>(null);

  // Fetch user-scoped templates with filters
  const {
    data: templatesData,
    isLoading: templatesLoading,
  } = useGetAdminNoteTemplatesQuery(
    {
      scope: 'user',
      limit: 100,
      offset: 0,
    },
    { skip: !open }
  );

  // Convert template mutation
  const [convertTemplate, { isLoading: isConverting }] = useConvertAdminNoteTemplateToOrgMutation();

  // Search for users to filter by
  const {
    data: userSearchResults,
    isLoading: userSearchLoading,
  } = useSearchProvidersQuery(userSearchQuery, {
    skip: !showUserSearch || userSearchQuery.trim().length === 0,
  });

  const templates = useMemo(() => {
    let allTemplates = templatesData?.templates || [];
    
    // Filter by user if selected
    if (userFilter) {
      allTemplates = allTemplates.filter(t => t.user_id === userFilter);
    }
    
    // Filter by search query (client-side search on title)
    if (searchQuery.trim()) {
      const queryLower = searchQuery.toLowerCase();
      allTemplates = allTemplates.filter(t =>
        t.title.toLowerCase().includes(queryLower) ||
        (t.description && t.description.toLowerCase().includes(queryLower))
      );
    }
    
    // Filter out templates that are already org-scoped for this organization
    return allTemplates.filter(t => !existingTemplateIds.includes(t.id));
  }, [templatesData, existingTemplateIds, userFilter, searchQuery]);

  const handleSelectUser = useCallback((userId: string, userName: string) => {
    setUserFilter(userId);
    setShowUserSearch(false);
    setUserSearchQuery(userName); // Show the user's name in the search field
    // RTK Query will automatically refetch when userId parameter changes
  }, []);

  const handleClearUserFilter = useCallback(() => {
    setUserFilter(null);
    setUserSearchQuery('');
    setShowUserSearch(false);
    // RTK Query will automatically refetch when userId parameter changes
  }, []);

  const handleSelectTemplate = useCallback((template: AdminNoteTemplateInfo) => {
    setSelectedTemplate(template);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedTemplate || !organizationId) return;

    try {
      await convertTemplate({
        adminConvertToOrgRequest: {
          template_id: selectedTemplate.id,
          organization_id: organizationId,
        },
      }).unwrap();

      enqueueSnackbar('Template converted to organization scope successfully', { variant: 'success' });
      onSelect(selectedTemplate.id);
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to convert template', { variant: 'error' });
    }
  }, [selectedTemplate, organizationId, convertTemplate, enqueueSnackbar, onSelect]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setUserFilter(null);
    setUserSearchQuery('');
    setShowUserSearch(false);
    setSelectedTemplate(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle>
        Convert User Template to Organization
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Browse and search user-created templates to convert them to organization scope
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
        {/* Search and Filter Controls */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search templates by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ position: 'relative', minWidth: 250 }}>
            <TextField
              fullWidth
              placeholder="Filter by user..."
              value={userSearchQuery}
              onChange={(e) => {
                setUserSearchQuery(e.target.value);
                setShowUserSearch(true);
              }}
              onFocus={() => userSearchQuery && setShowUserSearch(true)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                ),
                endAdornment: userFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearUserFilter}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* User Search Dropdown */}
            {showUserSearch && userSearchQuery && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  zIndex: 10,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
                elevation={3}
              >
                {userSearchLoading ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : userSearchResults && userSearchResults.length > 0 ? (
                  <List dense>
                    {userSearchResults.map((user: any) => (
                      <ListItemButton
                        key={user.provider_id}
                        onClick={() => handleSelectUser(user.provider_id, user.name || user.email)}
                      >
                        <ListItemText
                          primary={user.name || 'Unknown'}
                          secondary={user.email}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    No users found
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Selected Template Info */}
        {selectedTemplate && (
          <Alert
            severity="warning"
            icon={<Check />}
            action={
              <Button size="small" onClick={() => setSelectedTemplate(null)}>
                Change
              </Button>
            }
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Selected: {selectedTemplate.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This will permanently convert the template from user scope to organization scope. The original creator will be preserved.
            </Typography>
          </Alert>
        )}

        {/* Template List */}
        <Box sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {templatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {existingTemplateIds.length > 0 && templatesData?.templates && templatesData.templates.length > 0
                  ? 'All available user templates have already been converted to organization scope'
                  : searchQuery || userFilter
                  ? 'No user templates found matching your filters'
                  : 'No user templates available'}
              </Typography>
            </Box>
          ) : (
            <List>
              {templates.map((template, index) => (
                <Box key={template.id}>
                  {index > 0 && <Divider />}
                  <ListItemButton
                    selected={selectedTemplate?.id === template.id}
                    onClick={() => handleSelectTemplate(template)}
                    sx={{ py: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {template.title || 'Unnamed Template'}
                          </Typography>
                          <Chip
                            label="User Template"
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {template.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {template.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person sx={{ fontSize: 14 }} />
                              <Typography variant="caption">
                                User ID: {template.user_id}
                              </Typography>
                            </Box>
                            <Typography variant="caption">
                              Version: v{template.current_version}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" color="text.disabled">
                              ID: {template.id}
                            </Typography>
                            {template.updated_at && (
                              <Typography variant="caption" color="text.disabled">
                                Modified {formatRelativeTime(new Date(template.updated_at))}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
          )}
        </Box>

        {/* Result Count */}
        {!templatesLoading && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {templates.length} template{templates.length !== 1 ? 's' : ''} available
            {templatesData?.templates && templatesData.templates.length > templates.length && (
              <> ({templatesData.templates.length - templates.length} already converted)</>
            )}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isConverting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedTemplate || isConverting}
          startIcon={isConverting ? <CircularProgress size={16} /> : null}
        >
          {isConverting ? 'Converting...' : 'Convert to Org Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}